/**
 * IndustryRadar Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive project management interface that provides multiple views for triage project data.
 * Offers timeline pipeline, card grid, and table views with advanced filtering capabilities including
 * search, year/quarter filtering, and status-based filtering.
 * 
 * Features:
 * - Three display modes: Pipeline, Card View, Table View
 * - Advanced filtering (search, year, quarter, status)
 * - Dynamic quarter generation based on selected year
 * - Configurable page sizes for different views
 * - Project request modal integration
 * - Real-time data filtering and updates
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of project objects to display and filter
 * @returns {JSX.Element} The rendered industry radar component
 * 
 * @example
 * ```jsx
 * <IndustryRadar data={projectData} />
 * ```
 */

import React, { useState, useMemo } from 'react';
import ProjectRequestModal from '../../modals/ProjectRequestModal';
import TriageProjectTable from './TriageProjectTable';
import TriageProjectGrid from './TriageProjectGrid';
import TriageProjectPipeline from './TriageProjectPipeline';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import QuarterDropdown from '../Common/QuarterDropdown';
import { parseDate, removeTime, getQuarterFromDate } from '../../utils/textUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

import './IndustryRadar.css';

const IndustryRadar = ({ data }) => {
  /**
   * Get the current year as a string
   * 
   * @returns {string} Current year as string
   */
  const getCurrentYear = () => new Date().getFullYear().toString();
  
  const [showProjectRequestModal, setShowProjectRequestModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');

  // Page size states for Card View and Table View
  const [cardPageSize, setCardPageSize] = useState(6);
  const [tablePageSize, setTablePageSize] = useState(10);

  // Handlers for search, year, quarter, and status filters
  const handleSearchChange = (e) => setSearchQuery(e.target.value.toLowerCase());
  const handleYearChange = (e) => setSelectedYear(e.target.value);
  const handleStatusChange = (e) => setSelectedStatus(e.target.value);

  /**
   * Generate unique years from project data
   * 
   * Extracts years from both dateItemAddedtoTriage and completionDate fields,
   * creating a comprehensive list of years present in the dataset.
   * 
   * @returns {Array} Array of unique years, sorted, with 'All' as first option
   */
  const uniqueYears = useMemo(() => {
    const years = new Set();
    data.forEach(item => {
      const startDate = parseDate(item.dateItemAddedtoTriage);
      const endDate = parseDate(removeTime(item.completionDate));
      if (startDate && !isNaN(startDate)) years.add(startDate.getFullYear());
      if (endDate && !isNaN(endDate)) years.add(endDate.getFullYear());
    });
    return ['All', ...Array.from(years).map(year => year.toString()).sort()];
  }, [data]);

  /**
   * Generate unique statuses from project data
   * 
   * Extracts all unique status values from the dataset for filter dropdown.
   * 
   * @returns {Array} Array of unique statuses, sorted, with 'All' as first option
   */
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    data.forEach(item => {
      if (item.status) statuses.add(item.status);
    });
    return ['All', ...Array.from(statuses).sort()];
  }, [data]);

  /**
   * Generate unique quarters based on selected year
   * 
   * When a specific year is selected, generates quarters that actually contain data
   * for that year. Quarters are formatted as "Q1/23" style.
   * 
   * @returns {Array} Array of quarters available for the selected year
   */
  const uniqueQuarters = useMemo(() => {
    if (selectedYear === 'All') return [];
    const quarters = new Set();
    data.forEach(item => {
      const startDate = parseDate(item.dateItemAddedtoTriage);
      const endDate = parseDate(removeTime(item.completionDate));
      if (startDate && startDate.getFullYear().toString() === selectedYear) {
        quarters.add(`${getQuarterFromDate(startDate)}/${selectedYear.slice(-2)}`);
      }
      if (endDate && endDate.getFullYear().toString() === selectedYear) {
        quarters.add(`${getQuarterFromDate(endDate)}/${selectedYear.slice(-2)}`);
      }
    });
    return Array.from(quarters).sort();
  }, [data, selectedYear]);

  /**
   * Filter quarters based on data availability
   * 
   * Ensures that only quarters containing actual project data are shown
   * in the dropdown, preventing empty filter selections.
   * 
   * @returns {Array} Array of quarters that contain project data
   */
  const filteredQuarters = useMemo(() => {
    if (selectedYear === 'All') return uniqueQuarters;
    return uniqueQuarters.filter(quarter => {
      return data.some(item => {
        const startDate = parseDate(item.dateItemAddedtoTriage);
        const endDate = parseDate(removeTime(item.completionDate));
        const startQuarter = startDate ? `${getQuarterFromDate(startDate)}/${startDate.getFullYear().toString().slice(-2)}` : null;
        const endQuarter = endDate ? `${getQuarterFromDate(endDate)}/${endDate.getFullYear().toString().slice(-2)}` : null;
        return startQuarter === quarter || endQuarter === quarter;
      });
    });
  }, [uniqueQuarters, selectedYear, data]);

  /**
   * Apply all filters to the project data
   * 
   * Filters the data based on search query, year, quarter, and status selections.
   * Only shows Live and Completed projects.
   * Supports multiple field search including name, childName, and status.
   * 
   * @returns {Array} Filtered array of project objects
   */
  const filteredData = useMemo(() => {
    let filtered = data.filter(item => {
      // Only show Live and Completed projects
      if (!['Live', 'Completed'].includes(item.status)) {
        return false;
      }
      
      const nameMatch = item.name.toLowerCase().includes(searchQuery);
      const childNameMatch = item.childName?.toLowerCase().includes(searchQuery);
      const statusMatch = item.status?.toLowerCase().includes(searchQuery);
      return nameMatch || childNameMatch || statusMatch;
    });

    if (selectedYear !== 'All') {
      filtered = filtered.filter(item => {
        const startDate = parseDate(item.dateItemAddedtoTriage);
        const endDate = parseDate(removeTime(item.completionDate));
        
        // Check if either date falls within the selected year
        const startYear = startDate ? startDate.getFullYear().toString() : null;
        const endYear = endDate ? endDate.getFullYear().toString() : null;
        
        return startYear === selectedYear || endYear === selectedYear;
      });
    }

    if (selectedQuarter) {
      filtered = filtered.filter(item => {
        const startDate = parseDate(item.dateItemAddedtoTriage);
        const endDate = parseDate(removeTime(item.completionDate));
        
        if (!startDate || !endDate) return false;
        
        const startQuarter = `${getQuarterFromDate(startDate)}/${startDate.getFullYear().toString().slice(-2)}`;
        const endQuarter = `${getQuarterFromDate(endDate)}/${endDate.getFullYear().toString().slice(-2)}`;
        
        return startQuarter === selectedQuarter || endQuarter === selectedQuarter;
      });
    }

    if (selectedStatus !== 'All') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    return filtered;
  }, [data, searchQuery, selectedYear, selectedQuarter, selectedStatus]);

  // Dropdown options for Card and Table page sizes
  const cardPageOptions = [6, 12, 18, 24, 30, 36, 42, 48, 'All'];
  const tablePageOptions = [10, 20, 30, 40, 50, 'All'];

  return (
    <>
      <div id='triageprojecttabscontainer'>
        <Tabs selectedIndex={activeIndex} onSelect={(index) => setActiveIndex(index)}>
          <TabList>
            <Tab><i className="fa-solid fa-timeline"></i> Project Pipeline</Tab>
            <Tab><i className="fa-solid fa-border-all"></i> Card View</Tab>
            <Tab><i className="fa-solid fa-table-cells"></i> Table View</Tab>
          </TabList>
          <div className="filter-container">
            <div className="search-box-wrapper">
              <div className="search-input-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="filtersearch"
                />
              </div>
            </div>
            <div className="dropdown-container">
              <div className="year-dropdown">
                <select value={selectedYear} onChange={handleYearChange}>
                  {uniqueYears.map((year, index) => (
                    <option key={index} value={year}>
                      {year === 'All' ? 'Year (ALL)' : year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="quarter-dropdown">
                <QuarterDropdown
                  quarters={filteredQuarters}
                  selectedQuarter={selectedQuarter}
                  onQuarterChange={setSelectedQuarter}
                />
              </div>
              {/* <div className="status-dropdown">
                <select value={selectedStatus} onChange={handleStatusChange}>
                  {uniqueStatuses.map((status, index) => (
                    <option key={index} value={status}>
                      {status === 'All' ? 'Status (ALL)' : status}
                    </option>
                  ))}
                </select>
              </div> */}
              {/* Page size dropdown for Card and Table views */}
              {activeIndex === 1 && (
                <div className="page-size-dropdown">
                  <select value={cardPageSize} onChange={(e) => setCardPageSize(e.target.value)}>
                    {cardPageOptions.map((size, index) => (
                      <option key={index} value={size}>Show {size}</option>
                    ))}
                  </select>
                </div>
              )}
              {activeIndex === 2 && (
                <div className="page-size-dropdown">
                  <select value={tablePageSize} onChange={(e) => setTablePageSize(e.target.value)}>
                    {tablePageOptions.map((size, index) => (
                      <option key={index} value={size}>Show {size}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <TabPanel>
            <TriageProjectPipeline 
              data={filteredData} 
              isActive={activeIndex === 0}
              selectedYear={selectedYear}
              selectedQuarter={selectedQuarter}
            />
          </TabPanel>
          <TabPanel>
            <TriageProjectGrid data={filteredData} pageSize={cardPageSize} isActive={activeIndex === 1} />
          </TabPanel>
          <TabPanel>
            <TriageProjectTable data={filteredData} pageSize={tablePageSize} isActive={activeIndex === 2} />
          </TabPanel>
        </Tabs>
      </div>
    
      {showProjectRequestModal && (
        <ProjectRequestModal show={showProjectRequestModal} onClose={() => setShowProjectRequestModal(false)} />
      )}
    </>
  );
};

export default IndustryRadar;
